import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { tokenStorage, ApiError } from '../lib/api';
import Layout from '../components/Layout';

const API_BASE = import.meta.env.DEV 
  ? 'http://localhost:8787' 
  : 'https://reactor-jarvis-app.valleyintest.workers.dev';

interface Blueprint {
  id: string;
  sessionId: string;
  title: string;
  status: 'draft' | 'reviewed' | 'approved' | 'exported';
  version: number;
  createdAt: string;
  updatedAt: string;
  exportedAt?: string;
}

// 設計書API
const blueprintsApi = {
  list: async (): Promise<Blueprint[]> => {
    const token = tokenStorage.get();
    const res = await fetch(`${API_BASE}/api/blueprints`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) throw new ApiError(res.status, 'Failed to fetch blueprints');
    return res.json();
  },
};

export default function Blueprints() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: blueprints, isLoading } = useQuery({
    queryKey: ['blueprints'],
    queryFn: blueprintsApi.list,
  });

  const filteredBlueprints = blueprints?.filter(bp => {
    const matchesStatus = statusFilter === 'all' || bp.status === statusFilter;
    const matchesSearch = !searchQuery ||
      bp.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  }) || [];

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      reviewed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      exported: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    };
    const labels: Record<string, string> = {
      draft: '下書き',
      reviewed: 'レビュー済',
      approved: '承認済',
      exported: 'エクスポート済',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const handleExportMarkdown = async (blueprint: Blueprint) => {
    const token = tokenStorage.get();
    const res = await fetch(`${API_BASE}/api/sessions/${blueprint.sessionId}/blueprint`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (res.ok) {
      const data = await res.json();
      const blob = new Blob([data.markdownContent], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${blueprint.title.replace(/[^a-zA-Z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/g, '_')}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* ヘッダー */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              設計書一覧
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              Jarvisが生成した設計書（Blueprint）を管理できます
            </p>
          </div>
        </div>

        {/* フィルター */}
        <div className="card">
          <div className="flex flex-col md:flex-row gap-4">
            {/* 検索 */}
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                🔍
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="タイトルで検索..."
                className="input pl-10"
              />
            </div>

            {/* ステータスフィルター */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full md:w-48"
            >
              <option value="all">すべてのステータス</option>
              <option value="draft">下書き</option>
              <option value="reviewed">レビュー済</option>
              <option value="approved">承認済</option>
              <option value="exported">エクスポート済</option>
            </select>
          </div>
        </div>

        {/* 設計書一覧 */}
        <div className="card">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-jarvis-gold border-t-transparent rounded-full" />
            </div>
          ) : filteredBlueprints.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-2">📄</p>
              <p style={{ color: 'var(--text-secondary)' }}>
                {searchQuery || statusFilter !== 'all'
                  ? '条件に一致する設計書がありません'
                  : '設計書がまだありません'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Link to="/" className="btn-outline mt-4 inline-block">
                  新規ヒアリングを開始
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBlueprints.map((blueprint) => (
                <div
                  key={blueprint.id}
                  className="border rounded-lg p-4 hover:border-jarvis-gold transition-colors"
                  style={{ borderColor: 'var(--border-color)' }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                        {blueprint.title}
                      </h3>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                        v{blueprint.version} · {new Date(blueprint.createdAt).toLocaleDateString('ja-JP')}
                      </p>
                    </div>
                    {getStatusBadge(blueprint.status)}
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <Link
                      to={`/sessions/${blueprint.sessionId}/blueprint`}
                      className="btn-outline text-sm flex-1 text-center"
                    >
                      表示
                    </Link>
                    <button
                      onClick={() => handleExportMarkdown(blueprint)}
                      className="btn-primary text-sm flex-1"
                    >
                      📥 DL
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

